import React, { useState } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { MUITable } from 'components/shared/MUITable';
import { FinanceTableColumn, FinanceTableEntity } from '../../types';
import { APIRequestPagination, APIRequestSort, Nullable } from 'global';
import { FinanceFilters } from 'modules/finance/filters';
import { useFinanceData } from '../../hooks/useFinanceData';
import { useHistory } from 'react-router-dom';
import { VendorOpportunityTabs } from 'pages/VendorOpportunity/DetailPages/types';

interface Props {
  columns: FinanceTableColumn[];
  currentFilterConfig: Nullable<Partial<FinanceFilters>>;
  sorting: APIRequestSort;
  onSortingChange: (sorting: APIRequestSort) => void;
  onAggregatedMetricsChange: (aggregatedMetrics: {
    productsSoldCount: number;
    totalAftermarketProfit: number;
    totalFinanceInsuranceProfit: number;
  }) => void;
}

export const FinanceTable = ({
  columns,
  currentFilterConfig,
  sorting,
  onSortingChange,
  onAggregatedMetricsChange,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();

  const [pagination, setPagination] = useState<APIRequestPagination>({ page: 1, perPage: 25 });

  const { financeList, total, isLoading, productsSoldCount, totalAftermarketProfit, totalFinanceInsuranceProfit } =
    useFinanceData(currentFilterConfig, pagination, sorting);

  React.useEffect(() => {
    onAggregatedMetricsChange({ productsSoldCount, totalAftermarketProfit, totalFinanceInsuranceProfit });
  }, [productsSoldCount, totalAftermarketProfit, totalFinanceInsuranceProfit, onAggregatedMetricsChange]);

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: FinanceTableEntity) => {
    if (e.defaultPrevented) return;

    const isNeedOpenVoDetailsInNewBrowserTab = e.ctrlKey;
    const voDetailsPath = `/vo/${row.opportunityId}#${VendorOpportunityTabs.Revenue}`;

    if (isNeedOpenVoDetailsInNewBrowserTab) {
      window.open(voDetailsPath, '_blank');
    } else {
      history.push(voDetailsPath);
    }
  };

  return (
    <Box className={classes.root}>
      <MUITable<FinanceTableEntity>
        columns={columns}
        isLoading={isLoading}
        tableData={financeList}
        onRowClick={handleRowClick}
        hover={true}
        stickyHeader={true}
        pagination={{
          ...pagination,
          total,
          onChange: nextValue => setPagination(prev => ({ ...prev, ...nextValue })),
        }}
        sorting={{ ...sorting, onChange: onSortingChange }}
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    gap: '1rem',
    display: 'flex',
    flexDirection: 'column',

    '& .tableContainer': {
      height: 'calc(100vh - 309px)',
      '& .tableHead': {
        whiteSpace: 'nowrap',
      },
      '& .tableRow': {
        cursor: 'pointer',

        '&:nth-of-type(odd)': {
          backgroundColor: theme.palette.action.hover,
        },

        '& .MuiTableCell-root': {
          paddingTop: 2,
          paddingBottom: 2,
        },
      },
    },
  },
}));
